/**
 * Store de Protocolos - Zustand
 *
 * Maneja el estado global de los protocolos con:
 * - Persistencia automática en localStorage
 * - Acciones inmutables con Immer
 * - Selectores optimizados
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  Protocol,
  Frequency,
  Layer,
  CreateProtocolInput,
  UpdateProtocolInput,
} from '@/core/types';
import {
  generateProtocolId,
  generateSubtaskId,
  getNowISO,
  calculateDayProgress,
  filterByFrequency,
  groupByLayer,
} from '@/core/utils';
import { shouldReset } from '@/core/constants';
import { INITIAL_PROTOCOLS } from '@/data/initialProtocols';
import { useHistoryStore } from './historyStore';
import { useUIStore } from './uiStore';
import { updateStats, loadAchievementState } from '@/services/achievements';

interface ProtocolState {
  // === State ===
  protocols: Protocol[];
  activeFrequency: Frequency;
  isInitialized: boolean;
  lastAutoResetDate: string | null; // YYYY-MM-DD

  // === Actions: Subtasks ===
  toggleSubtask: (protocolId: string, subtaskId: string) => void;

  // === Actions: Protocols ===
  addProtocol: (input: CreateProtocolInput) => string;
  updateProtocol: (id: string, updates: UpdateProtocolInput) => void;
  deleteProtocol: (id: string) => void;
  toggleProtocolActive: (id: string) => void;

  // === Actions: Bulk ===
  resetByFrequency: (frequency: Frequency) => void;
  resetAll: () => void;
  checkAndResetStale: () => void;
  checkAndAutoReset: (autoResetTime: string) => void;

  // === Actions: UI ===
  setActiveFrequency: (frequency: Frequency) => void;

  // === Actions: Data ===
  importProtocols: (protocols: Protocol[]) => void;
  initialize: () => void;
}

export const useProtocolStore = create<ProtocolState>()(
  persist(
    immer((set, get) => ({
      // === Initial State ===
      protocols: [],
      activeFrequency: 'daily',
      isInitialized: false,
      lastAutoResetDate: null,

      // === Actions: Subtasks ===
      toggleSubtask: (protocolId, subtaskId) => {
        let justCompleted = false;
        let justUncompleted = false;
        let protocolForHistory: Protocol | null = null;
        let completedSubtaskIds: string[] = [];

        set((state) => {
          const protocol = state.protocols.find((p) => p.id === protocolId);
          if (!protocol) return;

          const subtask = protocol.subtasks.find((s) => s.id === subtaskId);
          if (!subtask) return;

          // Toggle subtask
          subtask.isDone = !subtask.isDone;

          // Update protocol completion status
          const allDone = protocol.subtasks.every((s) => s.isDone);
          const wasCompleted = protocol.isCompleted;
          protocol.isCompleted = allDone;

          // Detectar cambio de estado para historial
          if (allDone && !wasCompleted) {
            justCompleted = true;
            protocol.lastCompleted = getNowISO();
            protocol.streak += 1;
            // Clonar protocolo para historial (fuera del set)
            protocolForHistory = { ...protocol, subtasks: [...protocol.subtasks] };
            completedSubtaskIds = protocol.subtasks.filter((s) => s.isDone).map((s) => s.id);
          } else if (!allDone && wasCompleted) {
            justUncompleted = true;
          }

          // Update timestamp
          protocol.updatedAt = getNowISO();
        });

        // Registrar en historial (fuera del set para evitar dependencias circulares)
        if (justCompleted && protocolForHistory) {
          const uiState = useUIStore.getState();
          const mode = uiState.hardDayMode ? 'hardDay' : uiState.weekendMode ? 'weekend' : 'normal';
          useHistoryStore.getState().recordCompletion(protocolForHistory, completedSubtaskIds, mode);

          // Actualizar achievements
          const achievementState = loadAchievementState();
          updateStats({ totalCompletions: achievementState.stats.totalCompletions + 1 });

          // Verificar si es día perfecto (todos los protocolos diarios completados)
          const allProtocols = get().protocols;
          const dailyProtocols = allProtocols.filter(p => p.frequency === 'daily' && p.isActive);
          const allDailyComplete = dailyProtocols.every(p => p.isCompleted);
          if (allDailyComplete && dailyProtocols.length > 0) {
            const currentState = loadAchievementState();
            updateStats({ perfectDays: currentState.stats.perfectDays + 1 });
          }

          // Si estaba en modo día difícil y completó, registrar recuperación
          if (mode === 'hardDay') {
            const currentState = loadAchievementState();
            updateStats({ hardDaysRecovered: currentState.stats.hardDaysRecovered + 1 });
          }
        } else if (justUncompleted) {
          useHistoryStore.getState().recordUncompletion(protocolId);
        }
      },

      // === Actions: Protocols ===
      addProtocol: (input) => {
        const id = generateProtocolId();
        const now = getNowISO();

        const newProtocol: Protocol = {
          ...input,
          id,
          subtasks: input.subtasks.map((s, index) => ({
            ...s,
            id: generateSubtaskId(),
            isDone: false,
            order: index,
          })),
          isCompleted: false,
          streak: 0,
          createdAt: now,
          updatedAt: now,
          isCustom: true,
          isActive: true,
          reminderEnabled: input.reminderEnabled ?? false,
        };

        set((state) => {
          state.protocols.push(newProtocol);
        });

        return id;
      },

      updateProtocol: (id, updates) => {
        set((state) => {
          const protocol = state.protocols.find((p) => p.id === id);
          if (!protocol) return;

          Object.assign(protocol, updates, { updatedAt: getNowISO() });

          // Si se actualizan subtasks, recalcular completion
          if (updates.subtasks) {
            protocol.isCompleted = protocol.subtasks.every((s) => s.isDone);
          }
        });
      },

      deleteProtocol: (id) => {
        set((state) => {
          const index = state.protocols.findIndex((p) => p.id === id);
          if (index !== -1) {
            state.protocols.splice(index, 1);
          }
        });
      },

      toggleProtocolActive: (id) => {
        set((state) => {
          const protocol = state.protocols.find((p) => p.id === id);
          if (protocol) {
            protocol.isActive = !protocol.isActive;
            protocol.updatedAt = getNowISO();
          }
        });
      },

      // === Actions: Bulk ===
      resetByFrequency: (frequency) => {
        set((state) => {
          state.protocols.forEach((protocol) => {
            if (protocol.frequency === frequency) {
              protocol.subtasks.forEach((s) => {
                s.isDone = false;
              });
              protocol.isCompleted = false;
              protocol.updatedAt = getNowISO();
            }
          });
        });
      },

      resetAll: () => {
        set((state) => {
          state.protocols.forEach((protocol) => {
            protocol.subtasks.forEach((s) => {
              s.isDone = false;
            });
            protocol.isCompleted = false;
            protocol.updatedAt = getNowISO();
          });
        });
      },

      checkAndResetStale: () => {
        const now = new Date();
        set((state) => {
          state.protocols.forEach((protocol) => {
            if (
              protocol.isCompleted &&
              shouldReset(protocol.frequency, protocol.lastCompleted, now)
            ) {
              protocol.subtasks.forEach((s) => {
                s.isDone = false;
              });
              protocol.isCompleted = false;
              protocol.updatedAt = getNowISO();
            }
          });
        });
      },

      checkAndAutoReset: (autoResetTime: string) => {
        const now = new Date();
        const todayParts = now.toISOString().split('T');
        const today = todayParts[0] ?? now.toISOString().slice(0, 10); // YYYY-MM-DD
        const state = get();

        // Si ya reseteamos hoy, no hacer nada
        if (state.lastAutoResetDate === today) {
          return;
        }

        // Parsear la hora de reset (HH:mm)
        const [hours, minutes] = autoResetTime.split(':').map(Number);
        const resetHour = (hours ?? 4) + (minutes ?? 0) / 60;
        const currentHour = now.getHours() + now.getMinutes() / 60;

        // Si la hora actual es >= hora de reset, resetear protocolos diarios
        if (currentHour >= resetHour) {
          set((draft) => {
            // Solo resetear protocolos diarios que NO estén completados hoy
            // (si el lastCompleted es de ayer o antes)
            draft.protocols.forEach((protocol) => {
              if (protocol.frequency === 'daily') {
                // Resetear subtareas
                protocol.subtasks.forEach((s) => {
                  s.isDone = false;
                });
                protocol.isCompleted = false;
                protocol.updatedAt = getNowISO();
              }
            });
            draft.lastAutoResetDate = today;
          });
        }
      },

      // === Actions: UI ===
      setActiveFrequency: (frequency) => {
        set({ activeFrequency: frequency });
      },

      // === Actions: Data ===
      importProtocols: (protocols) => {
        set({ protocols });
      },

      initialize: () => {
        const state = get();

        // Si no hay protocolos, cargar los iniciales
        if (state.protocols.length === 0) {
          set({ protocols: INITIAL_PROTOCOLS, isInitialized: true });
          return;
        }

        // SIEMPRE sincronizar con INITIAL_PROTOCOLS para mantener datos actualizados
        set((draft) => {
          // Protocolos obsoletos que deben eliminarse (reemplazados por versiones nuevas)
          const deprecatedIds = [
            'prot-meals', // Reemplazado por prot-breakfast, prot-lunch, prot-snack, prot-dinner
            'prot-hygiene', // Reemplazado por prot-hygiene-am, prot-hygiene-pm
          ];

          // Eliminar protocolos obsoletos
          draft.protocols = draft.protocols.filter(
            (p) => !deprecatedIds.includes(p.id)
          );

          draft.protocols.forEach((existingProtocol) => {
            // Buscar el protocolo inicial correspondiente
            const initialProtocol = INITIAL_PROTOCOLS.find(
              (p) => p.id === existingProtocol.id
            );

            if (initialProtocol) {
              // SIEMPRE sincronizar campos de protocolo (forzado)
              if (initialProtocol.timeBlock) {
                existingProtocol.timeBlock = initialProtocol.timeBlock;
              }
              if (initialProtocol.idealTime) {
                existingProtocol.idealTime = initialProtocol.idealTime;
              }
              if (initialProtocol.rationale) {
                existingProtocol.rationale = initialProtocol.rationale;
              }
              if (initialProtocol.description) {
                existingProtocol.description = initialProtocol.description;
              }

              // SIEMPRE sincronizar subtareas con datos actualizados
              // Preservar el estado isDone del usuario, pero actualizar metadatos
              existingProtocol.subtasks = initialProtocol.subtasks.map((initialSub) => {
                const existingSub = existingProtocol.subtasks.find(
                  (s) => s.id === initialSub.id
                );

                return {
                  ...initialSub, // Tomar todos los campos actualizados (label, scientificNote, estimatedMinutes, etc.)
                  isDone: existingSub?.isDone ?? initialSub.isDone, // Preservar estado del usuario
                };
              });
            }
          });

          // Agregar protocolos nuevos que no existen en el estado guardado
          const existingIds = new Set(draft.protocols.map((p) => p.id));
          const newProtocols = INITIAL_PROTOCOLS.filter(
            (p) => !existingIds.has(p.id)
          );
          draft.protocols.push(...newProtocols);

          draft.isInitialized = true;
        });

        // Verificar y resetear protocolos vencidos
        get().checkAndResetStale();
      },
    })),
    {
      name: 'exocortex-protocols',
      version: 3, // v3: Forzar migración completa de subtasks
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        protocols: state.protocols,
        activeFrequency: state.activeFrequency,
        lastAutoResetDate: state.lastAutoResetDate,
      }),
      migrate: (persistedState, version) => {
        console.log('[Migration] Detected version:', version, '-> migrating to v3');
        const state = persistedState as { protocols: Protocol[] };

        if (state.protocols) {
          // Protocolos obsoletos que deben eliminarse
          const deprecatedIds = ['prot-meals', 'prot-hygiene'];
          state.protocols = state.protocols.filter(
            (p) => !deprecatedIds.includes(p.id)
          );

          // Sincronizar TODOS los protocolos con datos frescos
          state.protocols.forEach((existingProtocol) => {
            const initialProtocol = INITIAL_PROTOCOLS.find(
              (p) => p.id === existingProtocol.id
            );
            if (initialProtocol) {
              // Copiar campos de protocolo que puedan haber cambiado
              if (initialProtocol.timeBlock) {
                existingProtocol.timeBlock = initialProtocol.timeBlock;
              }
              if (initialProtocol.idealTime) {
                existingProtocol.idealTime = initialProtocol.idealTime;
              }
              if (initialProtocol.rationale) {
                existingProtocol.rationale = initialProtocol.rationale;
              }

              // Sincronizar subtareas preservando isDone
              existingProtocol.subtasks = initialProtocol.subtasks.map((initialSub) => {
                const existingSub = existingProtocol.subtasks.find(
                  (s) => s.id === initialSub.id
                );
                return {
                  ...initialSub,
                  isDone: existingSub?.isDone ?? initialSub.isDone,
                };
              });
            }
          });

          // Agregar protocolos nuevos que no existen
          const existingIds = new Set(state.protocols.map((p) => p.id));
          const newProtocols = INITIAL_PROTOCOLS.filter(
            (p) => !existingIds.has(p.id)
          );
          state.protocols.push(...newProtocols);

          console.log('[Migration] Completed. Total protocols:', state.protocols.length);
        }

        return persistedState as ProtocolState;
      },
    }
  )
);

// === Selectores ===

/**
 * Selector para protocolos activos por frecuencia
 * Nota: Este selector crea un nuevo array, usar con shallow comparison
 */
export const selectActiveByFrequency = (frequency: Frequency) => (state: ProtocolState) =>
  filterByFrequency(state.protocols, frequency);

/**
 * Selector para protocolos agrupados por capa
 * Nota: Este selector crea nuevos objetos, usar con shallow comparison
 */
export const selectGroupedByLayer = (frequency: Frequency) => (state: ProtocolState) =>
  groupByLayer(filterByFrequency(state.protocols, frequency));

/**
 * Selector para progreso del día - retorna valores primitivos para evitar re-renders
 */
export const selectDayProgress = (state: ProtocolState) => {
  const progress = calculateDayProgress(state.protocols, state.activeFrequency);
  return progress;
};

/**
 * Selector para un protocolo específico
 */
export const selectProtocolById = (id: string) => (state: ProtocolState) =>
  state.protocols.find((p) => p.id === id);

/**
 * Selector para protocolos de una capa
 */
export const selectByLayer = (layer: Layer) => (state: ProtocolState) =>
  state.protocols.filter((p) => p.layer === layer && p.isActive);

/**
 * Selector para estadísticas rápidas
 */
export const selectQuickStats = (state: ProtocolState) => {
  const active = state.protocols.filter((p) => p.isActive);
  const completed = active.filter((p) => p.isCompleted);
  const totalStreak = active.reduce((acc, p) => acc + p.streak, 0);

  return {
    total: active.length,
    completed: completed.length,
    percentage: active.length > 0 ? Math.round((completed.length / active.length) * 100) : 0,
    totalStreak,
    averageStreak: active.length > 0 ? Math.round(totalStreak / active.length) : 0,
  };
};
